import { db, getAllChaves, getAllHashes } from "./db";
import { encryptData, hasSessionKey } from "./crypto";
import type { NotaLocalRow, WorkerOut, IngestProgress, IngestDone } from "./types";
import { analyticsCache } from "@/services/repositories/analytics-cache";
import { useFiscalStore } from "@/store/fiscal-store";

export interface IngestCallbacks {
  onProgress?: (p: IngestProgress) => void;
  onDone?: (d: IngestDone) => void;
  onError?: (msg: string) => void;
}

let currentWorker: Worker | null = null;
let currentLoteId: string | null = null;
let currentEntity: { nome?: string; cnpj?: string } = {};

interface IncomingRow {
  row: Omit<NotaLocalRow, "id">;
  payload: any;
  hash: string;
  fileName: string;
  itensNorm: Array<{
    cProd: string; xProd: string; ncm: string; cfop: string; cst?: string;
    cest?: string; qCom: number; uCom?: string; vUnCom: number; vProd: number;
  }>;
}

async function persistRows(rows: IncomingRow[]) {
  if (!rows.length) return;
  const store = useFiscalStore.getState();
  const userId = store.userId;
  if (!userId) {
    console.error("[ingest] Tentativa de persistência sem userId ativo.");
    return;
  }

  const encrypted = await Promise.all(
    rows.map(async (r) => {
      const enc = hasSessionKey() ? await encryptData(r.payload) : null;
      return {
        ...r.row,
        userId,
        hash: r.hash,
        loteId: currentLoteId ?? undefined,
        cipher: enc?.cipher ?? null,
        iv: enc?.iv ?? null,
      } as NotaLocalRow;
    }),
  );

  // Transação atômica para garantir integridade entre notas e itens
  await db.transaction("rw", [db.notas, db.itens, db.analyticsCache], async () => {
    await db.notas.bulkPut(encrypted);

    const itensFlat: any[] = [];
    for (const r of rows) {
      for (const it of r.itensNorm) {
        itensFlat.push({ 
          documentoChave: r.row.chave, 
          userId, 
          perfilId: r.row.perfilId,
          ...it 
        });
      }
    }
    if (itensFlat.length) await db.itens.bulkPut(itensFlat);

    // Invalida cache analítico — novos dados chegaram
    await analyticsCache.invalidateByTipo("dashboard").catch(() => {});
  });
}

function spawnWorker(cb: IngestCallbacks): Worker {
  const w = new Worker(new URL("../../workers/xml-folder.worker.ts", import.meta.url), {
    type: "module",
  });
  currentWorker = w;

  w.onmessage = async (e: MessageEvent<WorkerOut | { type: "rows"; rows: IncomingRow[] }>) => {
    const msg = e.data as any;
    console.log(`[ingest] Mensagem do worker: ${msg.type}`, msg);
    
    if (msg.type === "progress") cb.onProgress?.(msg);
    else if (msg.type === "rows") {
      try {
        // Captura dinâmica da entidade se ainda estiver vazia
        if (!currentEntity.nome && msg.rows.length > 0) {
          const first = msg.rows[0].row;
          currentEntity = { 
            nome: first.emitente, 
            cnpj: first.cnpjEmit 
          };
        }
        await persistRows(msg.rows);
        console.log("[ingest] Batch de rows persistido.");
      } catch (err) {
        console.error("[ingest] Erro ao persistir rows:", err);
        cb.onError?.("Erro ao gravar no banco de dados local.");
      }
    }
    else if (msg.type === "done") {
      cb.onDone?.({ 
        ...msg, 
        loteId: currentLoteId,
        entidadeNome: currentEntity.nome,
        entidadeCnpj: currentEntity.cnpj
      });
    }
    else if (msg.type === "error") cb.onError?.(msg.message);
  };
  w.onerror = (e) => cb.onError?.(e.message);
  return w;
}

export async function ingestZipFile(file: File, cb: IngestCallbacks) {
  const store = useFiscalStore.getState();
  const userId = store.userId;
  if (!userId) return cb.onError?.("Usuário não identificado.");

  console.log("[ingest] Iniciando busca de chaves existentes...");
  const t0 = performance.now();
  try {
    await db.open(); // Garante que a migração v3 terminou
    const [existingKeys, existingHashes] = await Promise.all([
      getAllChaves(userId), 
      getAllHashes(userId)
    ]);
    console.log(`[ingest] Busca concluída em ${(performance.now() - t0).toFixed(0)}ms. Chaves: ${existingKeys.length}`);
    
    currentLoteId = crypto.randomUUID();
    currentEntity = { 
      nome: store.dashboard?.nomeEmpresa || store.perfilAtivo?.nome || "", 
      cnpj: store.dashboard?.cnpjEmpresa || store.perfilAtivo?.cnpj || ""
    };

    const w = spawnWorker(cb);
    w.postMessage({ type: "ingest-zip", file, existingKeys, existingHashes });
  } catch (err) {
    console.error("[ingest] Erro fatal antes do worker:", err);
    cb.onError?.(`Erro ao acessar banco de dados local: ${err instanceof Error ? err.message : String(err)}`);
  }
}

export async function ingestFiles(files: File[], cb: IngestCallbacks) {
  const store = useFiscalStore.getState();
  const userId = store.userId;
  if (!userId) return cb.onError?.("Usuário não identificado.");

  console.log("[ingest] Iniciando busca de chaves existentes...");
  const t0 = performance.now();
  try {
    await db.open(); // Garante que a migração v3 terminou
    const [existingKeys, existingHashes] = await Promise.all([
      getAllChaves(userId), 
      getAllHashes(userId)
    ]);
    console.log(`[ingest] Busca concluída em ${(performance.now() - t0).toFixed(0)}ms. Chaves: ${existingKeys.length}`);
    
    currentLoteId = crypto.randomUUID();
    currentEntity = { 
      nome: store.dashboard?.nomeEmpresa || store.perfilAtivo?.nome || "", 
      cnpj: store.dashboard?.cnpjEmpresa || store.perfilAtivo?.cnpj || ""
    };

    const w = spawnWorker(cb);
    w.postMessage({ type: "ingest-files", files, existingKeys, existingHashes });
  } catch (err) {
    console.error("[ingest] Erro fatal antes do worker:", err);
    cb.onError?.("Erro ao acessar banco de dados local.");
  }
}

/**
 * Lê uma pasta local recursivamente via File System Access API.
 * Filtra apenas .xml.
 */
export async function pickAndIngestDirectory(cb: IngestCallbacks) {
  // @ts-expect-error - File System Access API ainda parcial em TS lib
  if (!window.showDirectoryPicker) {
    cb.onError?.("Seu navegador não suporta seleção de pasta. Use Chrome, Edge ou Opera.");
    return;
  }
  
  try {
    // @ts-expect-error
    const dirHandle = await window.showDirectoryPicker();
    const store = useFiscalStore.getState();
    const userId = store.userId;

    if (userId) {
      await db.watchedDirectories.put({
        id: dirHandle.name,
        userId,
        handle: dirHandle,
        active: true,
        lastSync: Date.now()
      });
    }

    await syncDirectoryHandle(dirHandle, cb);
  } catch (err: any) {
    if (err.name === 'AbortError') return;
    cb.onError?.("Erro ao acessar pasta: " + err.message);
  }
}

export async function verifyPermission(handle: FileSystemHandle, readWrite = false) {
  const options: any = {};
  if (readWrite) options.mode = 'readwrite';
  
  // @ts-expect-error
  if ((await handle.queryPermission(options)) === 'granted') return true;
  // @ts-expect-error
  if ((await handle.requestPermission(options)) === 'granted') return true;
  return false;
}

export async function syncDirectoryHandle(dirHandle: FileSystemDirectoryHandle, cb: IngestCallbacks) {
  const files: File[] = [];

  async function walk(handle: any, path = "") {
    for await (const [name, entry] of handle.entries()) {
      if (entry.kind === "file" && (name.toLowerCase().endsWith(".xml") || name.toLowerCase().endsWith(".zip"))) {
        const f: File = await entry.getFile();
        try {
          Object.defineProperty(f, "name", { value: path + name });
        } catch { /* ignore */ }
        files.push(f);
      } else if (entry.kind === "directory") {
        await walk(entry, path + name + "/");
      }
    }
  }

  try {
    if (!(await verifyPermission(dirHandle))) {
      cb.onError?.("Permissão negada para acessar a pasta.");
      return;
    }
    
    await walk(dirHandle);

    if (!files.length) {
      cb.onError?.("Nenhum arquivo .xml ou .zip encontrado na pasta.");
      return;
    }

    await ingestFiles(files, {
      ...cb,
      onDone: (d) => {
        db.watchedDirectories.update(dirHandle.name, { lastSync: Date.now() });
        cb.onDone?.(d);
      }
    });
  } catch (err: any) {
    cb.onError?.("Erro na sincronização: " + err.message);
  }
}

export async function getWatchedDirectories() {
  const store = useFiscalStore.getState();
  const userId = store.userId;
  if (!userId) return [];
  return db.watchedDirectories.where("userId").equals(userId).toArray();
}

export function abortIngest() {
  currentWorker?.terminate();
  currentWorker = null;
}
